/**
 * Matrix placement — the rule that decides where a task appears on the Effort–Value
 * Matrix, extracted from `MatrixView` so it is testable as production code (2B part 2).
 *
 * THE DEFECTS THIS REPLACES
 * -------------------------
 * `MatrixView` derived both axes from the plan's own distribution, with fabricated
 * fallbacks at every degenerate edge:
 *
 *     if (gridTasks.length === 0) return { effortThreshold: 4, valueThreshold: 100000 };
 *     const medianEffort = efforts[Math.floor(efforts.length / 2)] || 4;
 *     const medianValue  = values[Math.floor(values.length / 2)]  || 100000;
 *
 * plus a ladder of degenerate-case bumps (`minEffort + 1`, `minValue + 0.5`, a 25th
 * percentile substitution) that manufactured a value split for plans that do not divide
 * by value at all. An empty plan was described against a $100,000 threshold nobody
 * supplied; a single priced task was "high value" because it equalled its own median.
 *
 * THE RULE NOW
 * ------------
 * - **Effort is canonical.** The boundary is the seam in the generator's own week bands
 *   (`estimateImplementationWeeks`: low 0.5–1, medium 2–4, high 4–12): `< 4` weeks is low
 *   effort, `>= 4` is high. It does not move with the plan, so it stays meaningful for one
 *   task, and a filter cannot shift it. Pinned to the server bands by
 *   `actionPlanMatrixBoundary.parity.test.ts`.
 * - **Value is a comparison, and the plan may not supply one.** `deriveValueSplit` returns
 *   a discriminated union — `split` (a real median over ≥2 distinct priced values),
 *   `single` (one priced task: nothing to compare against), `uniform` (all priced values
 *   identical: nothing divides them), or `none` (nothing priced: no threshold exists).
 *   Only `split` carries a threshold. There is NO fallback in any branch.
 * - **Three destinations, strict priority** (§6D item 4): unknown effort → Needs
 *   placement; otherwise unknown value → Value unavailable (beneath its real effort
 *   column); otherwise the grid. A task missing both goes to Needs placement only.
 * - **Filters never re-place.** The partition and the split are derived from the full,
 *   unfiltered rendered set; `matchesFilters` only decides visibility. The reconciliation
 *   tests assert both directions with task IDs, not counts.
 */

import { hasValue } from '../types/actionPlanValueProvenance';
import type { ValueProvenance, Quantified } from '../types/actionPlanValueProvenance';
import type { Task, TaskPriority, TaskStatusPersisted } from '../types/actionPlan';

// ============================================================================
// EFFORT — canonical generator bands, not plan statistics
// ============================================================================

/**
 * The low/high effort boundary in person-weeks: `< 4` is low, `>= 4` is high.
 *
 * This is the seam in `estimateImplementationWeeks`'s bands, where `medium` ends (2–4)
 * and `high` begins (4–12). It comes from the generator, not from this plan's median, so
 * the same task always claims the same column and a one-task plan still has a meaningful
 * effort axis. `actionPlanMatrixBoundary.parity.test.ts` fails if the server bands move.
 */
export const MATRIX_EFFORT_BOUNDARY_WEEKS = 4;

/**
 * The generator's three week bands, verbatim — for the threshold explainer ONLY.
 * Placement uses `MATRIX_EFFORT_BOUNDARY_WEEKS`; these exist so the UI can disclose
 * where the boundary comes from.
 */
export const EFFORT_BAND_WEEKS = {
  low: { min: 0.5, max: 1 },
  medium: { min: 2, max: 4 },
  high: { min: 4, max: 12 },
} as const;

// ============================================================================
// PARTITION — the three destinations
// ============================================================================

/** A task whose effort AND value are both known — the only shape the grid holds. */
export type GridTask = Task & {
  effort_weeks: number;
  value_usd: Quantified<number> & { value: number };
};

/** A task with known effort but no usable value — the Value unavailable band. */
export type ValueUnavailableTask = Task & { effort_weeks: number };

export interface MatrixPartition {
  /** Effort known, value known. Placed by `placeOnGrid`. */
  grid: GridTask[];
  /** Effort known, value unavailable. Listed beneath the correct effort column. */
  valueUnavailable: ValueUnavailableTask[];
  /** Effort unknown (value may be too). Cannot sit on either axis. */
  needsPlacement: Task[];
}

/**
 * THE FINITE-NUMBER BOUNDARY (36cd088 follow-up).
 *
 * A Matrix coordinate must be a finite number. `NaN` and `±Infinity` are arithmetic
 * accidents, and a runtime string is a wire accident — none of them is a measurement,
 * and treating any of them as "known" puts a card on an axis at a position that does
 * not exist. The S4 render boundary (`finiteOrNull`) already refuses to PRINT them;
 * these guards refuse to PLACE them, so a card can never read "Unavailable" while its
 * task is classified as priced or high-effort.
 *
 * The generic `hasValue` stays what it is — "the UI may render this number" per the
 * provenance invariant — and the finite check composes on top for coordinate use.
 */

/** Effort usable as a coordinate: a finite number. `0` is a measurement and qualifies. */
export function hasFiniteEffort(task: Task): task is Task & { effort_weeks: number } {
  return typeof task.effort_weeks === 'number' && Number.isFinite(task.effort_weeks);
}

/**
 * Value usable as a coordinate: passes the provenance gate AND is a finite number.
 * A `NaN` or `Infinity` claiming `observed` provenance is a malformed claim, not a
 * price; a genuine `$0` qualifies.
 */
export function hasFiniteValue(
  q: Quantified<number> | null | undefined,
): q is Quantified<number> & { value: number } {
  return hasValue(q) && typeof q.value === 'number' && Number.isFinite(q.value);
}

/**
 * Split the rendered tasks into their three destinations.
 *
 * STRICT PRIORITY (§6D item 4): unknown effort wins — a task missing both effort and
 * value appears in Needs placement only, never in Value unavailable as well. The three
 * sets are disjoint by construction and their union is the input; the reconciliation
 * test asserts both properties with IDs.
 *
 * "Unknown" includes malformed: a non-finite or non-numeric `effort_weeks` routes to
 * Needs placement, and a non-finite or non-numeric value with known effort routes to
 * Value unavailable — a coordinate that cannot be plotted is absent, whatever the wire
 * claims.
 *
 * A genuine `0` is data on both axes: zero weeks is a measurement (stays on the grid,
 * low effort), and zero dollars is a price (stays on the grid, compared like any other).
 */
export function partitionMatrixTasks(tasks: readonly Task[]): MatrixPartition {
  const grid: GridTask[] = [];
  const valueUnavailable: ValueUnavailableTask[] = [];
  const needsPlacement: Task[] = [];

  for (const task of tasks) {
    if (!hasFiniteEffort(task)) {
      needsPlacement.push(task);
    } else if (hasFiniteValue(task.value_usd)) {
      grid.push(task as GridTask);
    } else {
      valueUnavailable.push(task as ValueUnavailableTask);
    }
  }

  return { grid, valueUnavailable, needsPlacement };
}

// ============================================================================
// VALUE SPLIT — a comparison, or an honest refusal to compare
// ============================================================================

/**
 * How (and whether) the plan's priced tasks divide by value.
 *
 * Only `split` carries a threshold — the other kinds CANNOT expose one, which is the
 * type-level statement that a plan with nothing to compare has no dividing line. The
 * strict-assert file pins this: `threshold` must not exist on `single`, `uniform`, or
 * `none`, and consumers must handle all four kinds.
 */
export type ValueSplit =
  | {
      kind: 'split';
      /** The median priced value. Tasks at the median count as at-or-above (`>=`). */
      threshold: number;
      /** Priced tasks at or above the median — disclosed in the explainer. */
      atOrAboveCount: number;
      /** Priced tasks below the median — disclosed in the explainer. */
      belowCount: number;
    }
  | {
      /** Exactly one priced task: it equals its own median, which compares nothing. */
      kind: 'single';
      value: number;
    }
  | {
      /** Two or more priced tasks, all the same value: nothing divides them. */
      kind: 'uniform';
      value: number;
    }
  | {
      /** No priced tasks: no threshold exists. NOT $100,000. */
      kind: 'none';
    };

/**
 * Derive the value split from the FULL grid-eligible set (§6D item 3) — never from a
 * filtered subset, and never with a fallback.
 *
 * The median is the upper median (`values[floor(n/2)]` over the sorted list), the same
 * convention the previous code used minus its `|| 100000`. A lopsided but real
 * distribution (e.g. most tasks priced at the minimum) yields a lopsided but real split;
 * the counts are reported rather than "corrected" with a percentile bump.
 */
export function deriveValueSplit(grid: readonly GridTask[]): ValueSplit {
  if (grid.length === 0) return { kind: 'none' };

  const values = grid.map((t) => t.value_usd.value).sort((a, b) => a - b);
  if (grid.length === 1) return { kind: 'single', value: values[0] };
  if (values[0] === values[values.length - 1]) {
    return { kind: 'uniform', value: values[0] };
  }

  const threshold = values[Math.floor(values.length / 2)];
  let atOrAboveCount = 0;
  for (const v of values) if (v >= threshold) atOrAboveCount += 1;
  return {
    kind: 'split',
    threshold,
    atOrAboveCount,
    belowCount: values.length - atOrAboveCount,
  };
}

// ============================================================================
// GRID CELLS
// ============================================================================

/** The four comparative quadrants — legal ONLY under a real `split`. */
export type ComparativeCellId = 'quick-wins' | 'strategic-bets' | 'fill-ins' | 'major-projects';

/**
 * The non-comparative cells for `single` / `uniform` plans: the effort axis still
 * means something, the value axis deliberately claims nothing.
 */
export type NonComparativeCellId = 'unranked-low-effort' | 'unranked-high-effort';

export type MatrixCellId = ComparativeCellId | NonComparativeCellId;

/**
 * Place one grid task, given the plan-wide split.
 *
 * Under `split`: the four named quadrants, `value >= threshold` × `effort < 4`.
 * Under `single` / `uniform`: an effort-only cell that carries no value claim — a task is
 * NOT "high value" for equalling its own median, and a uniform plan is NOT split down a
 * line that does not exist.
 *
 * `none` is excluded from the signature: with no priced tasks there is nothing to place,
 * and accepting it would force this function to invent a cell for an impossible input.
 */
export function placeOnGrid(
  task: GridTask,
  split: Exclude<ValueSplit, { kind: 'none' }>,
): MatrixCellId {
  const lowEffort = task.effort_weeks < MATRIX_EFFORT_BOUNDARY_WEEKS;

  switch (split.kind) {
    case 'split': {
      const highValue = task.value_usd.value >= split.threshold;
      if (highValue) return lowEffort ? 'quick-wins' : 'strategic-bets';
      return lowEffort ? 'fill-ins' : 'major-projects';
    }
    case 'single':
    case 'uniform':
      return lowEffort ? 'unranked-low-effort' : 'unranked-high-effort';
    default: {
      // Exhaustiveness: a new ValueSplit kind must be handled here explicitly.
      const impossible: never = split;
      return impossible;
    }
  }
}

// ============================================================================
// DISPLAY GEOMETRY — the approved 3×2 grid (S4f-A, decision D1 as revised)
// ============================================================================

/**
 * The three effort columns. EXHAUSTIVE placement predicates — `<2` / `2–<4` /
 * `≥4` weeks — never band ranges like "0.5–1", which valid inputs (1.5 wk,
 * 4.0 wk) would contradict. The three columns REFINE the shipped comparative
 * boundary, never contradict it: `low` + `medium` together are exactly the
 * `< MATRIX_EFFORT_BOUNDARY_WEEKS` side (test-pinned).
 */
export type MatrixEffortColumn = 'low' | 'medium' | 'high';

export const MATRIX_EFFORT_COLUMNS: ReadonlyArray<{
  id: MatrixEffortColumn;
  label: string;
  /** The predicate as shown to the reader — the rule itself, not a range. */
  predicate: string;
}> = [
  { id: 'low', label: 'Low', predicate: '<2 wk' },
  { id: 'medium', label: 'Medium', predicate: '2–<4 wk' },
  { id: 'high', label: 'High', predicate: '≥4 wk' },
];

export function effortColumnOf(effortWeeks: number): MatrixEffortColumn {
  return effortWeeks < 2 ? 'low' : effortWeeks < 4 ? 'medium' : 'high';
}

/** The two value rows under a real split. `above` uses `>=` (at or above). */
export type MatrixValueRow = 'above' | 'below';

export function valueRowOf(
  task: GridTask,
  split: Extract<ValueSplit, { kind: 'split' }>,
): MatrixValueRow {
  return task.value_usd.value >= split.threshold ? 'above' : 'below';
}

/**
 * The approved cell vocabulary (gate rev 2). A below-median cell is never
 * called "Quick wins" — a cell whose row says "below the median" cannot
 * simultaneously claim its contents are wins.
 */
export const MATRIX_CELL_LABELS: Record<MatrixValueRow, Record<MatrixEffortColumn, string>> = {
  above: { low: 'Do first', medium: 'Schedule next', high: 'Plan carefully' },
  below: { low: 'When capacity allows', medium: 'Fill-in work', high: 'Defer' },
};

/**
 * Stable ordering inside every cell and band (S4f-A, D8): value descending —
 * the largest claim first — then title, then id, so ties cannot reorder
 * between renders and "+N more" always hides the same tail. Unpriced tasks
 * order by title, then id.
 */
export function stableTaskOrder(a: Task, b: Task): number {
  const av = hasFiniteValue(a.value_usd) ? a.value_usd.value : null;
  const bv = hasFiniteValue(b.value_usd) ? b.value_usd.value : null;
  if (av !== null && bv !== null && av !== bv) return bv - av;
  if (av !== null && bv === null) return -1;
  if (av === null && bv !== null) return 1;
  const byTitle = a.title.localeCompare(b.title);
  return byTitle !== 0 ? byTitle : a.id.localeCompare(b.id);
}

// ============================================================================
// PROVENANCE — the "Value source" disclosure
// ============================================================================

/**
 * The provenance a task's value USABLY claims.
 *
 * Plans stored before the provenance contract existed arrive without `value_usd`; that
 * is an absent value, and absence of a claim is `unavailable` — not a guess at what the
 * claim would have been. Likewise a malformed numeric (NaN, ±Infinity, a runtime
 * string): whatever provenance the wire asserts, there is no usable number for it to be
 * the provenance OF, so reporting "observed" or "AI estimate" for it would label an
 * unusable value as a measured one. Only a task with a finite value reports its claimed
 * provenance.
 */
export function provenanceOf(task: Task): ValueProvenance {
  return hasFiniteValue(task.value_usd) ? task.value_usd.provenance : 'unavailable';
}

/** Counts per provenance over a task set, for the toolbar's Value source filter. */
export function countByProvenance(tasks: readonly Task[]): Record<ValueProvenance, number> {
  const counts: Record<ValueProvenance, number> = {
    observed: 0,
    user_assumption: 0,
    reference_assumption: 0,
    model_estimate: 0,
    legacy_unverified: 0,
    unavailable: 0,
  };
  for (const task of tasks) counts[provenanceOf(task)] += 1;
  return counts;
}

// ============================================================================
// FILTERS — visibility only, never placement
// ============================================================================

/**
 * THE plan-wide filter object (S4f-B). Owned by `ActionPlanPanel` — one canonical
 * filter state that survives every view switch — and consumed identically by
 * Matrix, List and Timeline. (The name keeps its Matrix origin; the contract is
 * plan-wide.)
 */
export interface MatrixFilters {
  /** Case-insensitive substring over the task title. Empty string matches everything. */
  search: string;
  status: TaskStatusPersisted | 'all';
  priority: TaskPriority | 'all';
  /** Filter by what the value claims to be — see `PROVENANCE_LABEL`. */
  valueSource: ValueProvenance | 'all';
  /** A `Phase.id`, or all. */
  phase: string | 'all';
  /**
   * The value-calculation confidence level. A task with NO recorded confidence
   * matches only 'all' — filtering by High must not surface tasks whose
   * confidence was never assessed.
   */
  confidence: 'High' | 'Medium' | 'Low' | 'all';
}

export const DEFAULT_MATRIX_FILTERS: MatrixFilters = {
  search: '',
  status: 'all',
  priority: 'all',
  valueSource: 'all',
  phase: 'all',
  confidence: 'all',
};

/** The confidence a task's value calculation actually recorded, or null. */
export function taskConfidenceOf(task: Task): 'High' | 'Medium' | 'Low' | null {
  const level = task.value_calculation?.confidence_info?.level;
  return level === 'High' || level === 'Medium' || level === 'Low' ? level : null;
}

/** True when any filter would exclude anything a default state would not. */
export function anyFilterActive(filters: MatrixFilters): boolean {
  return (
    filters.search.trim() !== '' ||
    filters.status !== 'all' ||
    filters.priority !== 'all' ||
    filters.valueSource !== 'all' ||
    filters.phase !== 'all' ||
    filters.confidence !== 'all'
  );
}

/**
 * The composed filter predicate — exported so the reconciliation tests assert against
 * the SAME predicate the view renders with.
 *
 * Filtering decides which already-placed tasks are VISIBLE. It never feeds
 * `partitionMatrixTasks` or `deriveValueSplit`, so it cannot move a task between
 * destinations or shift a threshold (§6D item 5).
 */
export function matchesFilters(task: Task, filters: MatrixFilters): boolean {
  const search = filters.search.trim().toLowerCase();
  if (search !== '' && !task.title.toLowerCase().includes(search)) return false;
  if (filters.status !== 'all' && task.status !== filters.status) return false;
  if (filters.priority !== 'all' && task.priority !== filters.priority) return false;
  if (filters.valueSource !== 'all' && provenanceOf(task) !== filters.valueSource) return false;
  if (filters.phase !== 'all' && task.phase_id !== filters.phase) return false;
  if (filters.confidence !== 'all' && taskConfidenceOf(task) !== filters.confidence) return false;
  return true;
}
